import { mergeObjects } from "./utils";
var globalConfig = {
    key: "default"
}; // Empty default global config
var clientConfigs = {}; // Storage for client-specific configurations
/**
 * Sets or updates global configuration by clearing existing settings and applying new targeting parameters.
 * This function handles inputs with two possible parameter names for backward compatibility, prioritizing the
 * correctly spelled 'targetingParams'. It ensures a deep copy to avoid reference issues and sets a default
 * key if not already specified in the provided parameters.
 *
 * @param {Object} [targetingParams] - The correctly spelled parameter containing the new targeting settings.
 * @param {Object} [targettingParams] - The misspelled parameter, provided for backward compatibility.
 *
 * @example
 * // Example of setting targeting parameters using the correct parameter name
 * setTargetingParams({
 *   targetingParams: {
 *     user: "user123",
 *     region: "us-west"
 *   }
 * });
 *
 * // Backward compatibility example with misspelled parameter name
 * setTargetingParams({
 *   targettingParams: {
 *     user: "user123",
 *     region: "us-west"
 *   }
 * });
 */ function setTargetingParams(param) {
    var targetingParams = param.targetingParams, targettingParams = param.targettingParams;
    // Determine which parameter is provided. Prefer the correctly spelled `targetingParams`.
    var effectiveParams = targetingParams || targettingParams || {};
    // Clear existing properties in globalConfig
    Object.keys(globalConfig).forEach(function(key) {
        return delete globalConfig[key];
    });
    // Deep copy to avoid references, ensuring you're using the determined effective parameters
    Object.assign(globalConfig, JSON.parse(JSON.stringify(effectiveParams)));
    // Ensure there's always a default key if not provided
    if (!globalConfig.key) {
        globalConfig.key = "default";
    }
}
/**
 * Retrieves a deep copy of the current global configuration settings. This ensures that the returned
 * configuration is isolated from the original, preventing unintended modifications to the global state.
 *
 * @returns {Object} A deep copy of the global configuration settings.
 *
 * @example
 * // Example of retrieving global targeting parameters
 * const currentGlobalParams = getTargetingParams();
 * console.log(currentGlobalParams); // Outputs the current global configuration settings
 */ function getTargetingParams() {
    return JSON.parse(JSON.stringify(globalConfig));
}
/**
 * Sets or updates parameters specific to a client. This function initializes or resets the client's configuration
 * in the clientConfigs cache and then populates it with new parameters. It ensures that any existing parameters
 * are cleared before applying the new ones, thereby completely replacing the previous configuration.
 *
 * @param {string} clientId - The unique identifier for the client whose parameters are being set.
 * @param {Object} clientParams - An object containing the parameters to be set for the client.
 *
 * @example
 * // Example of setting client-specific parameters:
 * setClientParams({
 *   clientId: 'client123',
 *   clientParams: {
 *     custom: {myParam:"A"}
 *   }
 * });
 */ function setClientParams(param) {
    var clientId = param.clientId, clientParams = param.clientParams;
    if (!clientConfigs[clientId]) {
        clientConfigs[clientId] = {};
    }
    Object.keys(clientConfigs[clientId]).forEach(function(key) {
        return delete clientConfigs[clientId][key];
    }); // Clear existing properties
    Object.assign(clientConfigs[clientId], JSON.parse(JSON.stringify(clientParams)));
}
/**
 * Retrieves a deep copy of the configuration parameters for a specified client. If no configuration exists for the given client,
 * an empty object is returned. This ensures that any modifications to the returned object do not affect the original stored parameters.
 *
 * @param {string} clientId - The unique identifier for the client whose parameters are to be retrieved.
 * @returns {Object} A deep copy of the client's parameters or an empty object if no parameters are set.
 *
 * @example
 * // Example of retrieving client-specific parameters:
 * const clientSettings = getClientParams({ clientId: 'client123' });
 * console.log(clientSettings); // Outputs client-specific settings or an empty object
 */ function getClientParams(param) {
    var clientId = param.clientId;
    if (clientConfigs.hasOwnProperty(clientId)) {
        return JSON.parse(JSON.stringify(clientConfigs[clientId]));
    }
    return {}; // Return an empty object if no specific config for clientId
}
/**
 * Retrieves a merged object representing both the global configuration and a specified client's configuration.
 * If the client has specific configurations, they are merged with the global settings, with client settings
 * taking precedence over global ones. This function ensures that the returned object is a deep copy, preventing
 * any accidental modification of the stored configurations.
 *
 * @param {string} clientId - The unique identifier for the client whose combined parameters are to be retrieved.
 * @returns {Object} A new object containing the merged global and client-specific configurations.
 *
 * @example
 * // Example of retrieving combined global and client-specific parameters:
 * const combinedParams = getParamsForClient({ clientId: 'client123' });
 * console.log(combinedParams); // Outputs the merged configuration
 */ function getParamsForClient(param) {
    var clientId = param.clientId;
    var global = JSON.parse(JSON.stringify(globalConfig)); // Start with a copy of the global config
    if (clientConfigs.hasOwnProperty(clientId)) {
        var client = JSON.parse(JSON.stringify(clientConfigs[clientId])); // Dereference client object
        var combinedConfig = mergeObjects(global, client);
        return combinedConfig;
    }
    return global;
}
export { setTargetingParams, getTargetingParams, setClientParams, getClientParams, getParamsForClient };
